import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/settings/privacy')({
  component: PrivacySettingsPage,
})

type SkinScan = {
  id: string
  status: string
  createdAt: string
  deleteAfter?: string
}

type DeletionReceipt = {
  id: string
  objectType: string
  objectKey?: string
  deletedAt: string
}

function PrivacySettingsPage() {
  const [scans, setScans] = React.useState<SkinScan[]>([])
  const [receipts, setReceipts] = React.useState<DeletionReceipt[]>([])
  const [loading, setLoading] = React.useState(true)
  const [deleting, setDeleting] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true)
      const [scansRes, receiptsRes] = await Promise.all([
        fetch('/api/v1/skin-scans'),
        fetch('/api/v1/privacy/deletion-receipts'),
      ])

      if (scansRes.ok) {
        const scansData = await scansRes.json()
        setScans(Array.isArray(scansData) ? scansData : [])
      }

      if (receiptsRes.ok) {
        const receiptsData = await receiptsRes.json()
        setReceipts(Array.isArray(receiptsData) ? receiptsData : [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchData()
  }, [fetchData])

  async function handleDeleteScan(scanId: string) {
    if (!confirm('Are you sure? This will permanently delete your facial scan data.')) {
      return
    }

    setDeleting(scanId)
    try {
      const res = await fetch(`/api/v1/skin-scans/${scanId}`, { method: 'DELETE' })
      if (!res.ok) {
        throw new Error('Failed to delete scan')
      }

      setScans((prev) => prev.filter((s) => s.id !== scanId))
      const receipt = await res.json()
      setReceipts((prev) => [
        {
          id: receipt.id,
          objectType: 'skin_scan',
          deletedAt: receipt.deletedAt,
        },
        ...prev,
      ])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scan')
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <main className="ootopia-pwa-page ootopia-pwa-confirm ootopia-pwa-surface">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted">Loading privacy settings...</p>
      </main>
    )
  }

  return (
    <div className="ootopia-pwa-page ootopia-pwa-stack">
      <div>
        <h1 className="text-2xl font-bold ootopia-editorial-heading">Privacy Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your facial data and privacy preferences.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-danger/10 p-4">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Active Scans */}
      <section className="ootopia-pwa-surface p-5">
        <h2 className="font-semibold mb-4">Your Skin Scans</h2>
        {scans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active skin scans.</p>
        ) : (
          <div className="space-y-3">
            {scans.map((scan) => (
              <div key={scan.id} className="ootopia-pwa-privacy-row">
                <div>
                  <p className="font-medium">Saved skin scan</p>
                  <p className="text-sm text-muted">
                    Status: {scan.status} &middot; Created:{' '}
                    {new Date(scan.createdAt).toLocaleDateString()}
                  </p>
                  {scan.deleteAfter && (
                    <p className="text-xs text-muted">
                      Scheduled deletion: {new Date(scan.deleteAfter).toLocaleString()}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteScan(scan.id)}
                  disabled={deleting === scan.id}
                  className="rounded-lg bg-danger px-4 py-2 text-sm text-danger-foreground disabled:opacity-50"
                >
                  {deleting === scan.id ? 'Deleting...' : 'Delete Now'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Deletion Receipts */}
      <section className="ootopia-pwa-surface p-5">
        <h2 className="font-semibold mb-4">Deletion History</h2>
        {receipts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No deletion history.</p>
        ) : (
          <div className="space-y-2">
            {receipts.map((receipt) => (
              <div key={receipt.id} className="ootopia-pwa-privacy-row">
                <div>
                  <p className="text-sm font-medium">{receipt.objectType}</p>
                  <p className="text-xs text-muted">
                    Deleted: {new Date(receipt.deletedAt).toLocaleString()}
                  </p>
                </div>
                <span className="rounded-full bg-success/10 px-3 py-1 text-xs text-success">
                  Deleted
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Info */}
      <section className="ootopia-pwa-disclosure">
        <p className="text-xs text-muted leading-relaxed">
          <strong>Your Privacy:</strong> When you delete a skin scan, all associated facial data is
          permanently removed from our servers. This includes the original selfie, analysis results,
          and any derived data. Deletion is immediate and cannot be undone.
        </p>
      </section>
    </div>
  )
}
